import { SoundButton } from "../../common/SoundButton/SoundButton";
// RewardedReferralContent.tsx

import energyCanCase from "../../../assets/static/images/drawers/purchase-energy-bundle/energy-can-case.webp";
import energyCan from "../../../assets/static/images/symbols/v1/symbol-energy-drink.webp";
import beachArea from "../../../assets/static/images/decos/drawers/rewarded-referral/beach-area-deco.webp";
import palmLeafLeft from "../../../assets/static/images/decos/drawers/rewarded-referral/palm-leaf-left-deco.webp";
import palmLeafRight from "../../../assets/static/images/decos/drawers/rewarded-referral/palm-leaf-right-deco.webp";
import OutlineStrokedShareIcon from "../../../assets/static/icons/outline-stroked-share-icon-2.svg?react";
import FatLockIcon from "../../../assets/static/icons/fat-lock-icon.svg?react";
import { ButtonVar1 } from "../../common/ButtonVar1/ButtonVar1";
import { Trans, useTranslation } from "react-i18next";
import "./RewardedReferralContent.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowLeft,
    faArrowRotateRight,
} from "@awesome.me/kit-f3e20eab48/icons/classic/solid";
import { haptics } from "../../../logic/helpers/haptics";

interface RewardedReferralContentProps {
    qualifiedCount: number;
    requiredCount: number;
    rewardAmount: string;
    isRewarded: boolean;
    onBack: () => void;
    onRefresh: () => void;
    onShare: () => void;
    refreshDisabled?: boolean;
    shareDisabled?: boolean;
    shareLabel?: string;
    message?: string | null;
}

export function RewardedReferralContent({
    qualifiedCount,
    requiredCount,
    rewardAmount,
    isRewarded,
    onBack,
    onRefresh,
    onShare,
    refreshDisabled = false,
    shareDisabled = false,
    shareLabel,
    message,
}: RewardedReferralContentProps) {
    const { t } = useTranslation();

    return (
        <section className="rewarded-referral-content" tabIndex={-1} autoFocus>
            <div className="rewarded-referral-content__toolbar">
                <SoundButton
                    type="button"
                    aria-label={t("rewardedReferral.backAriaLabel")}
                    onClick={() => {
                        haptics.impact("light");
                        onBack();
                    }}
                >
                    <FontAwesomeIcon
                        icon={faArrowLeft}
                        className="rewarded-referral-content__toolbar__back-icon"
                    />
                </SoundButton>
                <SoundButton
                    type="button"
                    aria-label={t("rewardedReferral.refreshAriaLabel")}
                    disabled={refreshDisabled}
                    onClick={() => {
                        haptics.impact("light");
                        onRefresh();
                    }}
                >
                    <FontAwesomeIcon
                        icon={faArrowRotateRight}
                        className="rewarded-referral-content__toolbar__refresh-icon"
                    />
                </SoundButton>
            </div>

            <strong
                className="rewarded-referral-content__progress"
                dir="ltr"
                aria-label={t("rewardedReferral.progressAriaLabel", {
                    qualifiedCount,
                    requiredCount,
                })}
            >
                {qualifiedCount}/{requiredCount}
            </strong>

            <p className="rewarded-referral-content__copy">
                <Trans
                    i18nKey="rewardedReferral.progressCopy"
                    components={{
                        friends: <span />,
                        opened: <span />,
                        linebreak: <br />,
                    }}
                />
            </p>

            <div className="rewarded-referral-content__reward">
                <div
                    className="rewarded-referral-content__reward__reward-art"
                    aria-label={t("rewardedReferral.rewardAriaLabel", {
                        amount: rewardAmount,
                    })}
                >
                    <div className="rewarded-referral-content__reward__reward-art__case-wrapper">
                        <img
                            className="rewarded-referral-content__reward__reward-art__case"
                            src={energyCanCase}
                            alt=""
                            aria-hidden="true"
                            draggable={false}
                        />
                        <img
                            className="rewarded-referral-content__reward__reward-art__can"
                            src={energyCan}
                            alt=""
                            aria-hidden="true"
                            draggable={false}
                        />
                        <strong dir="ltr" data-text={`x${rewardAmount}`}>
                            x{rewardAmount}
                        </strong>
                    </div>
                </div>

                <div
                    className={[
                        "rewarded-referral-content__reward__unlock",
                        isRewarded && "is-unlocked",
                    ]
                        .filter(Boolean)
                        .join(" ")}
                >
                    <FatLockIcon className="rewarded-referral-content__reward__unlock__icon" />

                    <p>
                        {isRewarded
                            ? t("rewardedReferral.caseUnlocked")
                            : t("rewardedReferral.unlockCase")}
                    </p>
                </div>
            </div>

            {message && (
                <p className="rewarded-referral-content__message" role="status">
                    {message}
                </p>
            )}

            <ButtonVar1
                className="rewarded-referral-content__share"
                // text={shareLabel ?? t("rewardedReferral.share")}
                disabled={shareDisabled}
                onClick={() => {
                    haptics.impact("light");
                    onShare();
                }}
                content={
                    <div className="rewarded-referral-content__share__content">
                        <OutlineStrokedShareIcon />
                        <p>{shareLabel ?? t("rewardedReferral.share")}</p>
                    </div>
                }
            />

            <img
                className="rewarded-referral-content__palm rewarded-referral-content__palm--left"
                src={palmLeafLeft}
                alt=""
                aria-hidden="true"
                draggable={false}
            />
            <img
                className="rewarded-referral-content__palm rewarded-referral-content__palm--right"
                src={palmLeafRight}
                alt=""
                aria-hidden="true"
                draggable={false}
            />
            <img
                className="rewarded-referral-content__beach"
                src={beachArea}
                alt=""
                aria-hidden="true"
                draggable={false}
            />
        </section>
    );
}
